/* globals functionality, views, components, showModalConfirm, showModalError*/
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import DataTable, { Column } from "common/DataTable";
import { isIPv4IPv6Range, isValidIPValue, safeStr } from "common/api";

const createPlaceholder = () =>
  `ADDRESS`;

const propagateHeaderIntoRowType = input => {
  const [head, ...rows] = input.trim().split('\n');
  const type = 
    head === 'ADDRESS' ? 'IP-ADDRESS' : 
    head === 'RANGE' ? 'IP-ADDR-RANGE' : 
    head === 'VLAN' ? 'VLAN-ID' : 
    head // SUBSCRIBER-ID
  return rows.map( row => `${type} ${row}`);
} 

const ensurePlaceholder = () => (error) => {
  return { response: createPlaceholder() };
};

const doIpAddressesRequest = (subscriberGroup) =>
  Promise.all([
    ifCl.run(`show api subscriber-group name ${safeStr(subscriberGroup)} subscriber`), 
    ifCl.run(`show api subscriber-group name ${safeStr(subscriberGroup)} subscriber-range`),
    ifCl.run(`show api subscriber-group name ${safeStr(subscriberGroup)} subscriber-id`),
    ifCl.run(`show api subscriber-group name ${safeStr(subscriberGroup)} vlan`)
  ])
  .then((responses) => { 
    const response = [
      'TYPE VALUE',
      ...responses.flatMap(propagateHeaderIntoRowType)
    ].join('\n');
    return { response }
  })
  .catch(ensurePlaceholder());

const ResultsLayout = styled.div`
  margin-right: 5px;
  flex: 100%;
  overflow: hidden; /*list will scroll*/
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  --is-highest: #3d85c6;
  --is-higher: #6aa84f;
  --is-lower: #e69138;
  --is-lowest: #cc0000;
  --warning: #cc0000;
  .inverted {
    --is-lowest: #3d85c6;
    --is-lower: #6aa84f;
    --is-higher: #e69138;
    --is-highest: #cc0000;
  }
  .dataTable tbody td {
    transition: color 1s, background-color: 1s;
  }
  &.colorize-warnings .dataTable tbody td {
    &.warning {
      color: white;
      background: var(--warning);
    }
  }
`;

const ListValues = ({ request, subscriberGroup }) => {
  const actions = useContext(ActionsContext);
  const reloadWhenDone = () => actions.send("do-load-subscriber-groups-edit");

  const openSubscriberDetails = (target) => {
    const returnTo = 'viewStatusSubscriberGroupsEdit';
    views.doKeep(returnTo);
    globalNavigate("viewSubsDetail", {returnView: returnTo, ...target});
  };
  
  return <TableContainer
    className={"colorize-warnings"}
  >
    <Request during={request}>
      {({response}) => (
        <DataTable
          pageLength={25}
          exportAsCSV={true}
          content={response}
          columns={[
            Column.Text({
              label: "TYPE",
              idx: 0,
              title: "Type"
            }),
            Column.Text({
              label: "VALUE",
              idx: 1,
              title: "Value",
              cellClassName: (value, [type]) => ['IP-ADDRESS', 'SUBSCRIBER-ID'].includes(type) ?'hyperlink-text':'',
              onClick: (value, [type]) => {
                if(type === 'IP-ADDRESS'){
                  openSubscriberDetails({ addr: value })
                }else if(type === 'SUBSCRIBER-ID'){
                  openSubscriberDetails({ subsId: value })
                }
              },
            }),
            Column.Actions({
              label: "ACTIONS",
              idx: 1,
              colClassName: "text-center action",
              cellClassName: "hides-content-to-operators",
              are: [
                {
                  icon: "delete",
                  label: "Delete",
                  onClick: (value, [type]) => {
                    showModalConfirm(
                      `Delete ${value} from subscriber group?`,
                      "",
                      "delete_forever",
                      () => {
                        let clearCmd = '';
                        switch(type){
                          case 'IP-ADDRESS':
                            clearCmd = `clear api subscriber ${value} subscriber-group ${safeStr(subscriberGroup)}`
                            break;
                          case 'SUBSCRIBER-ID':
                            clearCmd = `clear api subscriber-group ${safeStr(subscriberGroup)} subscriber-id ${value}`
                            break;
                          case 'VLAN-ID':
                            clearCmd = `clear api subscriber-group ${safeStr(subscriberGroup)} vlan ${value}`
                            break;
                          case 'IP-ADDR-RANGE':
                            clearCmd = `clear api subscriber-group ${safeStr(subscriberGroup)} subscriber-range ${value}`
                            break;
                        }

                        ifCl
                          .run(clearCmd)
                          .then(reloadWhenDone)
                          .catch((error) => showModalError(error));
                      }
                    );
                  },
                },
              ],
            }),
          ]}
        />
      )}
    </Request>
  </TableContainer>
};

export const ValuesTable = ({subscriberGroup}) => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);

  const doLoad = () => setRequest(doIpAddressesRequest(subscriberGroup));

  useEffect(() => {
    doLoad();
    return actions.recv("do-load-subscriber-groups-edit", doLoad);
  }, []);
  
  return request === null ? null : (
      <ResultsLayout>
        <ListValues request={request} subscriberGroup={subscriberGroup} />
      </ResultsLayout>
  );
};
